import React, {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { buildOutline } from 'app/services/formula/expressionOutline';
import { lintModel } from 'app/services/formula/ruleLinter';
import { getExpressionReadback, getSignatureHelp } from 'app/services/formula/suggestionEngine';
import { parseTextToModel } from 'app/services/formula/visualBuilderModel';
import { NODE_KIND } from 'app/services/formula/visualBuilderModel/visualBuilderModel.constants';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { HelperText, Label } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';

import LintPanel from '../VisualExpressionBuilder/components/LintPanel';

import AdvancedEditorPanel from './components/AdvancedEditorPanel';
import {
  expressionLanguageConfig,
  expressionMonarchDefinition,
  expressionThemeConfig,
  expressionThemeConfigDark,
  getEditorConstructionOptions,
  languageId as baseLanguageId,
  themeId,
  themeIdDark,
} from './ExpressionEditor.constants';
import {
  errorTooltipStyles,
  getCustomBoxStyles,
  labelRowStyles,
  modeSwitchStyles,
  roleBadgeStyles,
} from './ExpressionEditor.styles';
import { ExpressionEditorTestIds } from './ExpressionEditor.testIds';
import {
  formatExpressionDocument,
  getHelperText,
  getModelPositionFromFlatPosition,
} from './ExpressionEditor.utils';

const VisualExpressionBuilder = lazy(() => import('../VisualExpressionBuilder'));

const VIEW_MODE = {
  VISUAL: 'visual',
  ADVANCED: 'advanced',
};

/**
 * Return true when the parsed model is fully visual (no RAW nodes anywhere).
 *
 * @param {{ text: string, schemaParams: Object, isOnlyBooleanAllowed: boolean, isNewExpressionsParsingLogicEnabled: boolean }} params
 * @returns {boolean}
 */
const isFullyVisualResult = ({
  text,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  const result = parseTextToModel({
    text,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
  });

  return result?.isFullyVisual === true;
};

/**
 * Determine the initial view mode.
 * When forceAdvancedMode is set, always returns 'advanced' (the toggle is hidden).
 * Otherwise visual mode is offered when:
 *  - isOnlyBooleanAllowed is true and the expression parses to a visual model, OR
 *  - the expression is a SWITCH expression (value-returning decision table), OR
 *  - the expression is fully parseable to a visual model (no RAW nodes).
 *
 * @param {{ value: string, schemaParams: Object, isOnlyBooleanAllowed: boolean, isNewExpressionsParsingLogicEnabled: boolean, forceAdvancedMode: boolean }} params
 * @returns {'visual'|'advanced'}
 */
const resolveInitialMode = ({
  value,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
  forceAdvancedMode,
}) => {
  if (forceAdvancedMode) {
    return VIEW_MODE.ADVANCED;
  }

  if (!isOnlyBooleanAllowed) {
    const hasValue = !!(value && value.trim());

    if (hasValue) {
      const fullyVisual = isFullyVisualResult({
        text: value,
        schemaParams,
        isOnlyBooleanAllowed,
        isNewExpressionsParsingLogicEnabled,
      });

      return fullyVisual ? VIEW_MODE.VISUAL : VIEW_MODE.ADVANCED;
    }

    return VIEW_MODE.ADVANCED;
  }

  const result = parseTextToModel({
    text: value,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
  });

  return result ? VIEW_MODE.VISUAL : VIEW_MODE.ADVANCED;
};

const ExpressionEditor = ({
  id = '',
  disabled = false,
  readOnly = false,
  required = false,
  value: formValue,
  error = false,
  helperText = '',
  label,
  name,
  schemaParams,
  isOnlyBooleanAllowed = false,
  forceAdvancedMode = false,
  onChange,
  staticValidateField,
  dataTestId = '',
  menu = null,
}) => {
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const { palette } = useTheme();
  const activeThemeId = palette.mode === 'dark' ? themeIdDark : themeId;

  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const schemaParamsRef = useRef({});

  const languageId = `${baseLanguageId}-${name}`;

  const [isMonacoInstanceReady, setIsMonacoInstanceReady] = useState(false);
  const [debouncedFormValue, setDebouncedFormValue] = useState(formValue);

  const [viewMode, setViewMode] = useState(() =>
    resolveInitialMode({
      value: formValue,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
      forceAdvancedMode,
    }),
  );

  // Adjust state during render (not in an effect) when the parent flips forceAdvancedMode at
  // runtime — e.g. Determination → Value — so there is no one-frame flash of the visual builder.
  if (forceAdvancedMode && viewMode !== VIEW_MODE.ADVANCED) {
    setViewMode(VIEW_MODE.ADVANCED);
  }

  const wrapperProps = useMemo(() => ({ id, 'data-testid': dataTestId }), [id, dataTestId]);

  const roleBadgeText = isOnlyBooleanAllowed
    ? 'Boolean gate · returns true/false'
    : 'Formula · returns a value';

  const readbackText = useMemo(
    () =>
      getExpressionReadback({
        expressionText: debouncedFormValue,
        schemaParams,
        isNewExpressionsParsingLogicEnabled,
      }),
    [debouncedFormValue, schemaParams, isNewExpressionsParsingLogicEnabled],
  );

  const parsedModel = useMemo(
    () =>
      parseTextToModel({
        text: debouncedFormValue,
        schemaParams,
        isOnlyBooleanAllowed,
        isNewExpressionsParsingLogicEnabled,
      }),
    [debouncedFormValue, schemaParams, isOnlyBooleanAllowed, isNewExpressionsParsingLogicEnabled],
  );

  const isSwitchExpression = useMemo(
    () => parsedModel?.model?.kind === NODE_KIND.SWITCH,
    [parsedModel],
  );

  const lintFindings = useMemo(
    () => (parsedModel ? lintModel(parsedModel.model) : []),
    [parsedModel],
  );

  const outlineNodes = useMemo(
    () =>
      buildOutline({
        text: debouncedFormValue,
        schemaParams,
        isNewExpressionsParsingLogicEnabled,
      }),
    [debouncedFormValue, schemaParams, isNewExpressionsParsingLogicEnabled],
  );

  const onOutlineNodeClick = useCallback(node => {
    if (node.start === null || !editorRef.current || !monacoRef.current) {
      return;
    }

    const modelValue = editorRef.current.getValue();
    const monacoRange = getModelPositionFromFlatPosition({
      modelValue,
      position: { start: node.start, end: node.end ?? node.start },
    });

    editorRef.current.setSelection(monacoRange);
    editorRef.current.revealRangeInCenter(monacoRange);
    editorRef.current.focus();
  }, []);

  useEffect(() => {
    const shouldValidate = viewMode === VIEW_MODE.ADVANCED ? isMonacoInstanceReady : true;

    if (!shouldValidate || _.isEmpty(formValue)) {
      return;
    }

    staticValidateField?.(name, formValue, null, { isDeferredUntilSchema: true });

    // for initial validation only so there is no need to add formValue to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonacoInstanceReady, viewMode, name, staticValidateField]);

  useEffect(() => {
    if (!monacoRef.current || _.isEqual(schemaParamsRef.current, schemaParams)) {
      return;
    }

    schemaParamsRef.current = schemaParams;

    const signatureProvider = monacoRef.current.languages.registerSignatureHelpProvider(
      languageId,
      {
        signatureHelpTriggerCharacters: ['(', ','],
        provideSignatureHelp: (model, position) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          const help = getSignatureHelp({ textBeforeCursor, schemaParams });

          if (_.isNil(help)) {
            return null;
          }

          return {
            value: {
              signatures: [
                {
                  label: help.label,
                  documentation: help.documentation,
                  parameters: help.parameters,
                },
              ],
              activeSignature: 0,
              activeParameter: help.activeParameter,
            },
            dispose: _.noop,
          };
        },
      },
    );

    return () => {
      signatureProvider?.dispose();
    };
  }, [
    isMonacoInstanceReady,
    name,
    schemaParams,
    isOnlyBooleanAllowed,
    languageId,
    isNewExpressionsParsingLogicEnabled,
  ]);

  const handleEditorWillMount = useCallback(
    monaco => {
      monaco.languages.register({ id: languageId });

      monaco.languages.setLanguageConfiguration(languageId, expressionLanguageConfig);

      monaco.languages.setMonarchTokensProvider(languageId, expressionMonarchDefinition);

      const expressionDocumentFormattingProvider = {
        provideDocumentFormattingEdits: formatExpressionDocument,
      };
      monaco.languages.registerDocumentFormattingEditProvider(
        languageId,
        expressionDocumentFormattingProvider,
      );

      monaco.editor.defineTheme(themeId, expressionThemeConfig);
      monaco.editor.defineTheme(themeIdDark, expressionThemeConfigDark);
    },
    [languageId],
  );

  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    setIsMonacoInstanceReady(true);
  }, []);

  useEffect(() => {
    if (!monacoRef.current || !editorRef.current) {
      return;
    }

    const model = editorRef.current.getModel();

    const isPositionInfoExists = helperText.indexOf('[') > 0;

    const [start, end] = isPositionInfoExists
      ? helperText.slice(helperText.indexOf('[') + 1, helperText.indexOf(']')).split(',')
      : [null, null];

    const modelPosition = isPositionInfoExists
      ? getModelPositionFromFlatPosition({
          modelValue: model.getValue(),
          position: { start: Number(start), end: Number(end) },
        })
      : { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 };

    monacoRef.current.editor.setModelMarkers(model, `${languageId}-linter`, [
      {
        message: helperText,
        severity: monacoRef.current.MarkerSeverity.Error,
        ...modelPosition,
      },
    ]);
  }, [helperText, languageId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFormValue(formValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [formValue]);

  const onChangeValue = useCallback(
    newValue => {
      // noboolean is used to prevent useForm string to boolean conversion
      onChange({ target: { name, value: newValue, dataset: { noboolean: true } } });
    },
    [onChange, name],
  );

  const onSwitchToAdvanced = useCallback(() => {
    setViewMode(VIEW_MODE.ADVANCED);
  }, []);

  const onSwitchToVisual = useCallback(() => {
    const result = parseTextToModel({
      text: formValue,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    });

    if (!result) {
      return;
    }

    setViewMode(VIEW_MODE.VISUAL);
  }, [formValue, schemaParams, isOnlyBooleanAllowed, isNewExpressionsParsingLogicEnabled]);

  const onVisualUnparseable = useCallback(() => {
    setViewMode(VIEW_MODE.ADVANCED);
  }, []);

  const isVisualMode = viewMode === VIEW_MODE.VISUAL;

  const editorOptions = useMemo(
    () => getEditorConstructionOptions({ readOnly: readOnly || disabled }),
    [readOnly, disabled],
  );

  return (
    <Box sx={getCustomBoxStyles(disabled)}>
      {(label || menu) && (
        <Box sx={labelRowStyles}>
          {label && (
            <Label error={error} name={name} required={required} withRequiredIcon={required}>
              {label}
            </Label>
          )}
          <Box sx={roleBadgeStyles}>
            <Typography color="text.secondary" variant="regular12">
              {roleBadgeText}
            </Typography>
          </Box>
          {!forceAdvancedMode &&
            (isOnlyBooleanAllowed ||
              isSwitchExpression ||
              (parsedModel?.isFullyVisual && !!debouncedFormValue?.trim())) && (
              <Box sx={modeSwitchStyles}>
                <Button
                  color={isVisualMode ? 'primary' : 'inherit'}
                  data-testid={ExpressionEditorTestIds.VISUAL_MODE_BUTTON}
                  size="small"
                  variant={isVisualMode ? 'contained' : 'outlined'}
                  onClick={onSwitchToVisual}
                >
                  Visual
                </Button>
                <Button
                  color={!isVisualMode ? 'primary' : 'inherit'}
                  data-testid={ExpressionEditorTestIds.ADVANCED_MODE_BUTTON}
                  size="small"
                  variant={!isVisualMode ? 'contained' : 'outlined'}
                  onClick={onSwitchToAdvanced}
                >
                  Advanced
                </Button>
              </Box>
            )}
          {menu}
        </Box>
      )}

      {isVisualMode ? (
        <Suspense fallback={null}>
          <VisualExpressionBuilder
            disabled={disabled || readOnly}
            isNewExpressionsParsingLogicEnabled={isNewExpressionsParsingLogicEnabled}
            isOnlyBooleanAllowed={isOnlyBooleanAllowed}
            name={name}
            schemaParams={schemaParams}
            value={formValue}
            onChange={onChange}
            onUnparseable={onVisualUnparseable}
          />
        </Suspense>
      ) : (
        <AdvancedEditorPanel
          beforeMount={handleEditorWillMount}
          editorOptions={editorOptions}
          editorRef={editorRef}
          editorValue={formValue}
          helperText={helperText}
          isFullyVisual={parsedModel?.isFullyVisual}
          isNewExpressionsParsingLogicEnabled={isNewExpressionsParsingLogicEnabled}
          isOnlyBooleanAllowed={isOnlyBooleanAllowed}
          languageId={languageId}
          lintFindings={lintFindings}
          monacoRef={monacoRef}
          outlineNodes={outlineNodes}
          readbackText={readbackText ?? ''}
          schemaParams={schemaParams}
          themeId={activeThemeId}
          wrapperProps={wrapperProps}
          onChangeValue={onChangeValue}
          onMount={handleEditorDidMount}
          onOutlineNodeClick={onOutlineNodeClick}
        />
      )}

      {isVisualMode && <LintPanel findings={lintFindings} />}

      {isVisualMode && (!formValue || !formValue.trim()) && !error ? (
        <Typography color="text.secondary" variant="regular12">
          Add at least one condition to complete this rule.
        </Typography>
      ) : (
        <TooltipWrapper title={helperText} tooltipSx={errorTooltipStyles}>
          <Box>
            <HelperText error={error} isTruncated={false}>
              {getHelperText(helperText)}
            </HelperText>
          </Box>
        </TooltipWrapper>
      )}
    </Box>
  );
};

ExpressionEditor.propTypes = {
  schemaParams: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  staticValidateField: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  dataTestId: PropTypes.string,
  isOnlyBooleanAllowed: PropTypes.bool,
  forceAdvancedMode: PropTypes.bool,
  menu: PropTypes.node,
};

export default memo(ExpressionEditor);
